import {XIcon} from '@primer/octicons-react'
import {IconButton, Spinner} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {useRef} from 'react'
import {NewMarkdownViewer} from '@github-ui/markdown-viewer/NewMarkdownViewer'
import type {SafeHTMLString} from '@github-ui/safe-html'

type OverviewSidePanelProps = {
  bodyHtml: SafeHTMLString
  isLoading: boolean
  onClose: () => void
}

/**
 * Renders the overview side panel for pull requests
 */
export function OverviewSidePanel({bodyHtml, isLoading, onClose}: OverviewSidePanelProps) {
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const toggleSidesheetRef = useRef<HTMLButtonElement>(null)

  // Check if this is the empty description state by looking for the "No description provided" text
  const isEmptyDescription = bodyHtml.includes('No description provided')

  return (
    <Dialog
      initialFocusRef={closeButtonRef}
      position={{narrow: 'fullscreen', regular: 'right', wide: 'right'}}
      returnFocusRef={toggleSidesheetRef}
      onClose={onClose}
      renderHeader={({dialogLabelId}) => (
        <Dialog.Header className="p-3">
          <div className="d-flex flex-row flex-justify-between flex-items-center width-full">
            <Dialog.Title id={dialogLabelId}>Overview</Dialog.Title>
            <IconButton
              ref={closeButtonRef}
              aria-label="Close overview panel"
              icon={XIcon}
              variant="invisible"
              onClick={onClose}
            />
          </div>
        </Dialog.Header>
      )}
    >
      <div className="p-4">
        {isLoading ? (
          <div className="d-flex flex-justify-center">
            <Spinner />
          </div>
        ) : (
          <div className={isEmptyDescription ? 'd-flex flex-justify-start' : 'd-flex flex-justify-center'}>
            <NewMarkdownViewer verifiedHTML={bodyHtml} />
          </div>
        )}
      </div>
    </Dialog>
  )
}

try{ OverviewSidePanel.displayName ||= 'OverviewSidePanel' } catch {}